import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { Eye } from "lucide-react";
import { listContacts, updateContactStatus, exportContactsCsv } from "@/lib/api/contacts.functions";
import { DataTable } from "@/components/admin/DataTable";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { ContactEnquiryDetailDialog } from "@/components/admin/ContactEnquiryDetailDialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/contacts/")({
  loader: () => listContacts({ data: {} }),
  component: AdminContactsPage,
});

type Row = Awaited<ReturnType<typeof listContacts>>[number];

function AdminContactsPage() {
  const data = Route.useLoaderData();
  const router = useRouter();
  const [selected, setSelected] = useState<Row | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  async function openEnquiry(row: Row) {
    setSelected(row);
    setDialogOpen(true);
    if (row.status === "new") {
      try {
        await updateContactStatus({ data: { id: row.id, status: "read" } });
        router.invalidate();
      } catch {
        /* viewing still works if status update fails */
      }
    }
  }

  const columns: ColumnDef<Row>[] = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "email", header: "Email" },
    { accessorKey: "company", header: "Company", meta: { filterVariant: "text" } },
    {
      accessorKey: "source",
      header: "Source",
      meta: { filterVariant: "select", groupable: true },
    },
    {
      accessorKey: "status",
      header: "Status",
      meta: { filterVariant: "select", groupable: true },
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: "createdAt",
      header: "Date",
      cell: ({ row }) => (row.original.createdAt ? new Date(row.original.createdAt).toLocaleString() : ""),
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      enableGrouping: false,
      cell: ({ row }) => (
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={() => openEnquiry(row.original)}>
            <Eye className="mr-1.5 h-3.5 w-3.5" />
            View
          </Button>
          {row.original.status === "new" && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                await updateContactStatus({ data: { id: row.original.id, status: "read" } });
                toast.success("Marked as read");
                router.invalidate();
              }}
            >
              Mark read
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={async () => {
              await updateContactStatus({ data: { id: row.original.id, status: "archived" } });
              toast.success("Archived");
              router.invalidate();
            }}
          >
            Archive
          </Button>
        </div>
      ),
    },
  ];

  async function handleExport() {
    const { csv } = await exportContactsCsv();
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "enquiries.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="Contact enquiries" />
      <DataTable tableId="contacts" columns={columns} data={data} onExportCsv={handleExport} />
      <ContactEnquiryDetailDialog
        enquiry={selected}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </AdminPageShell>
  );
}
